export const searchLandingPages = {
  "smart-qr": {
    kind: "product",
    eyebrow: "Smart QR Solutions",
    title: "Smart QR Codes That Connect Physical Products to Digital Experiences",
    metaTitle: "Smart QR Solutions for Business | Mayr Technologies",
    metaDescription: "Create managed Smart QR experiences for products, locations, campaigns, support, and customer engagement with Mayr Technologies.",
    summary: "Turn signs, packaging, equipment, cards, and printed material into measurable digital touchpoints that can keep working after printing.",
    audience: "Businesses that want a simple bridge between physical customer touchpoints and useful, updateable digital content.",
    benefits: [
      "Send customers to product information, support, booking, contact, or campaign pages.",
      "Use clear destinations designed for mobile visitors instead of generic homepages.",
      "Create a foundation for measurable campaigns, attribution, and future QR management tools.",
      "Connect Smart QR activity with Mayr Technologies automation and content workflows.",
    ],
    process: [
      "Define the customer action the QR experience should produce.",
      "Build a focused mobile destination with one clear call to action.",
      "Apply branded QR artwork to the physical product or campaign.",
      "Measure visits and improve the destination as real usage data becomes available.",
    ],
    cta: "Plan a Smart QR project",
    externalUrl: null,
    related: ["business-automation", "custom-software"],
  },
  "pro-email": {
    kind: "product",
    eyebrow: "Professional Email Platform",
    title: "Professional Email Campaign Infrastructure Without Bloated SaaS Overhead",
    metaTitle: "Professional Email Marketing Platform | Pro Email",
    metaDescription: "Prepare and manage professional email campaigns with Pro Email, a Mayr Technologies platform designed for practical business communication workflows.",
    summary: "Pro Email gives organizations a focused environment for preparing professional email communication and connecting campaign work with the rest of their operating process.",
    audience: "Businesses, agencies, schools, and public-sector organizations that want a practical email campaign platform and clearer operational ownership.",
    benefits: [
      "Prepare campaigns, reusable content, and professional communication workflows.",
      "Connect approved contacts and campaign preparation with Mayr Lead Creator.",
      "Keep campaign operations focused without assembling a large collection of disconnected tools.",
      "Use Mayr Ops to support controlled social promotion and campaign content around the platform.",
    ],
    process: [
      "Define the audience, business purpose, and approved communication content.",
      "Prepare the campaign and organize the required contact information.",
      "Review the message and campaign settings before sending.",
      "Evaluate results and refine the next campaign using real performance information.",
    ],
    cta: "Explore Pro Email",
    externalUrl: "https://pro-email.app",
    related: ["lead-creator", "business-automation"],
  },
  "lead-creator": {
    kind: "product",
    eyebrow: "B2B Campaign Preparation",
    title: "Build Better B2B Campaigns With Organized Lead Research",
    metaTitle: "B2B Lead Research and Campaign Preparation | Mayr Lead Creator",
    metaDescription: "Use Mayr Lead Creator to organize B2B target-company and contact research for controlled sales and email campaign preparation.",
    summary: "Mayr Lead Creator helps teams prepare B2B outreach by bringing target-company research, contact organization, and campaign preparation into one structured workflow.",
    audience: "Agencies and B2B teams that need a clearer process for identifying prospects and preparing campaigns before outreach begins.",
    benefits: [
      "Organize target companies and contacts around a defined campaign objective.",
      "Review and qualify campaign data before it moves into an outreach platform.",
      "Reduce repetitive switching between research files, spreadsheets, and campaign tools.",
      "Connect approved campaign preparation with Pro Email and related Mayr workflows.",
    ],
    process: [
      "Define the target market, company profile, and campaign purpose.",
      "Research and organize suitable companies and contacts.",
      "Review the information and approve the campaign group.",
      "Transfer the approved preparation into the selected outreach workflow.",
    ],
    cta: "Explore Lead Creator",
    externalUrl: "https://lead-creator.mayr-technologies.com",
    related: ["pro-email", "business-automation"],
  },
  "business-automation": {
    kind: "solution",
    eyebrow: "Business Automation",
    title: "Practical Business Automation Built Around Your Real Workflow",
    metaTitle: "Custom Business Automation Solutions | Mayr Technologies",
    metaDescription: "Reduce repetitive coordination and connect business processes with custom automation designed around the way your organization actually works.",
    summary: "We design controlled automation that handles repeatable work while keeping people responsible for decisions that require judgment, approval, or customer context.",
    audience: "Organizations that lose time to repetitive follow-ups, disconnected tools, duplicate data entry, and unclear handoffs.",
    benefits: [
      "Map the existing process before choosing technology.",
      "Connect data and actions across the tools your team already uses.",
      "Add visible approval points instead of hiding important decisions inside automation.",
      "Measure where work slows down and improve the workflow over time.",
    ],
    process: [
      "Identify the repetitive work and the business outcome that matters.",
      "Document ownership, exceptions, approvals, and required data.",
      "Build a focused automation with clear controls and auditability.",
      "Launch carefully, measure results, and extend only where it creates value.",
    ],
    cta: "Discuss an automation project",
    externalUrl: null,
    related: ["custom-software", "smart-qr"],
  },
  "custom-software": {
    kind: "solution",
    eyebrow: "Custom Software Development",
    title: "Custom Software That Fits the Business Instead of Forcing the Business to Fit",
    metaTitle: "Custom Software Development | Mayr Technologies",
    metaDescription: "Mayr Technologies designs custom software, SaaS platforms, internal tools, integrations, and operational systems around real business requirements.",
    summary: "When generic software creates workarounds, duplicated effort, or expensive subscriptions, a focused custom platform can give the business a simpler operating model.",
    audience: "Businesses with specialized operations, integration requirements, or valuable workflows that standard software cannot support cleanly.",
    benefits: [
      "Translate business requirements into a clear, maintainable product scope.",
      "Build internal tools, customer portals, SaaS platforms, and system integrations.",
      "Preserve operational ownership with visible data, permissions, and audit trails.",
      "Deliver in controlled stages so useful results arrive before unnecessary complexity.",
    ],
    process: [
      "Clarify the commercial goal and the current operational constraints.",
      "Design the smallest complete workflow that creates measurable value.",
      "Build, test, and review the product with the people who will use it.",
      "Deploy, monitor, and expand based on evidence from real operation.",
    ],
    cta: "Plan custom software",
    externalUrl: null,
    related: ["business-automation", "communications-systems"],
  },
  "communications-systems": {
    kind: "solution",
    eyebrow: "Communication Systems",
    title: "Connected SIP, Voice, Video, Entry, and AV Communication Systems",
    metaTitle: "SIP, Voice, Video and AV Integration | Mayr Technologies",
    metaDescription: "Design and integrate SIP, voice, video, entry, and AV communication systems with Mayr Technologies.",
    summary: "Mayr Technologies combines software, communication infrastructure, and control integration to create reliable systems for real operational environments.",
    audience: "Organizations that need voice, video, SIP, entry communication, AV control, or connected communication workflows designed as one system.",
    benefits: [
      "Design communication workflows across SIP, voice, video, entry, and AV systems.",
      "Integrate software and hardware around the requirements of the environment.",
      "Provide clear operational controls, monitoring, and support paths.",
      "Build custom components when off-the-shelf products cannot complete the workflow.",
    ],
    process: [
      "Document users, locations, devices, call flows, and operational requirements.",
      "Design the system architecture and integration boundaries.",
      "Configure and test the communication and control workflow.",
      "Deploy with documentation, monitoring, and a practical support plan.",
    ],
    cta: "Discuss a communication system",
    externalUrl: null,
    related: ["custom-software", "business-automation"],
  },
};

export const featuredSearchPages = [
  { slug: "smart-qr", label: "Smart QR", path: "/products/smart-qr" },
  { slug: "pro-email", label: "Pro Email", path: "/products/pro-email" },
  { slug: "lead-creator", label: "Lead Creator", path: "/products/lead-creator" },
  { slug: "business-automation", label: "Business Automation", path: "/solutions/business-automation" },
  { slug: "custom-software", label: "Custom Software", path: "/solutions/custom-software" },
  { slug: "communications-systems", label: "Communication Systems", path: "/solutions/communications-systems" },
];

export const landingPathForSlug = (slug) => {
  const item = featuredSearchPages.find((page) => page.slug === slug);
  return item?.path || "/";
};
